/**
 * HTTP helpers for the WFM backend (moved from the old common/common.js — behaviour unchanged).
 * Endpoints live in app/services/endpoints.js.
 */
import { toast } from "react-toastify";
import axios from "axios";
import config from "app/services/endpoints";

const authHeaders = () => ({
  "Content-Type": "application/json",
  "cyient-access-token": sessionStorage.getItem("jwtToken"),
  "CSRF-Token": sessionStorage.getItem("csrfToken"),
});

/** Toast helper used across the app: misAlert("success" | "error" | "warning" | "info" | "information", message). */
export async function misAlert(type, message) {
  const t = type.toLowerCase();
  const toastType = t === "information" ? "info" : ["info", "success", "warning", "error"].includes(t) ? t : "default";
  toast(message, { type: toastType, theme: "colored" });
}

/** Unauthenticated POST with callbacks (used by the login screens). */
export async function secureAjaxCallWithCallback(serviceUrl, inputData, successFunction, failFunction) {
  try {
    const response = await fetch(serviceUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(inputData),
    });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    successFunction(data);
  } catch (error) {
    failFunction(error);
    misAlert("error", `An error has occurred: ${error.message}`);
  }
}

/** Authenticated POST; resolves to the response body. */
export async function postData(ApiUrl, inputData) {
  const response = await axios.post(ApiUrl, inputData, { headers: authHeaders() });
  return response.data;
}

/** Authenticated POST (fetch, with cookies); resolves to `result.data`. */
export const populateDataInList = async (serviceUrl, inputdata) => {
  try {
    const response = await fetch(serviceUrl, {
      method: "POST",
      headers: authHeaders(),
      credentials: "include",
      body: JSON.stringify(inputdata),
    });
    if (!response.ok) throw new Error("Network response was not ok");
    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

/** Runs a named ProcessQuery action and passes the rows to setData. */
export const fetchFilterDrops = async (action, queryFields, setData) => {
  try {
    const response = await populateDataInList(config.ProcessQuery.url, { action, AndOr: "and", queryFields });
    setData(response || []);
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/** Whole table via the cursor endpoint → setData(rows). */
export const fetchTabledata = async (tableName, setData) => {
  try {
    const res = await postData(config.ProcessQueryCursor.url, { action: "gettabledata", "values[]": tableName });
    setData(Array.isArray(res?.data?.[1]) ? res.data[1] : []);
  } catch (error) {
    console.error("Error fetching processes:", error);
  }
};

/** Table rows where column = value. Calls setData(rows) if given, otherwise returns the rows. */
export const fetchTabledataColum = async (tableName, Columnname, valu, setData) => {
  try {
    const res = await postData(config.ProcessQueryCursor.url, {
      action: "gettabledatawithcolum",
      "values[]": [tableName, Columnname, valu],
    });
    const result = Array.isArray(res?.data?.[1]) ? res.data[1] : [];
    if (typeof setData === "function") setData(result);
    else return result;
  } catch (error) {
    console.error("Error fetching processes:", error);
    if (!setData) return [];
  }
};

/**
 * One server-side page from /api/data/ProcessDataTable (SELECT … LIMIT/OFFSET + COUNT(*)).
 * Small and fast, so it never hits Azure's 230s idle timeout. Returns { data, totalcount }.
 */
export async function fetchDataPage({ columns, filter = "", table, start = 0, limit = 50, sort }) {
  const response = await postData(config.ProcessDataTable.url, { columns, filter, table, start, limit, sort });
  return {
    data: Array.isArray(response?.data) ? response.data : [],
    totalcount: Number(response?.totalcount) || 0,
  };
}

export const refreshMVData = async () => {
  try {
    await postData(config.ProcessQueryWitVals.url, { action: "getrefeshmv", values: "mvjobsdata" });
    return true;
  } catch (error) {
    misAlert("error", "Error refreshing data");
    return false;
  }
};

/** Fetches a view in chunks and downloads it as <tablename>.xlsx. `xlsx` is loaded on demand. */
export async function downloadExcelBatched(tablename, view, finalFilter = "", totalRowCount = 1000, chunkSize = 1000, sort) {
  try {
    if (!sessionStorage.getItem("jwtToken")) {
      console.error("JWT token is missing! Cannot download Excel.");
      return;
    }
    let allData = [];
    for (let start = 0; start < totalRowCount; start += chunkSize) {
      const response = await axios.post(
        config.ProcessDataTable.url,
        { table: view, filter: finalFilter, sort, start, limit: Math.min(chunkSize, totalRowCount - start) },
        { headers: authHeaders() }
      );
      const chunk = response.data?.data;
      if (Array.isArray(chunk)) allData = allData.concat(chunk);
    }
    if (!allData.length) {
      console.warn("No data returned for Excel download.");
      return;
    }
    const XLSX = await import("xlsx");
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(allData), "Sheet1");
    XLSX.writeFile(workbook, `${tablename}.xlsx`);
  } catch (error) {
    console.error("Excel generation failed:", error);
  }
}
